// src/components/MediaCard.tsx

import { MediaItem } from "../types/media.types";


export const MediaCard = ({
  item,
  onDelete,
  onSelect,
  onClose,
  excludedExtentions
}: {
  item: MediaItem;
  onDelete?: (id: string) => void;
  onSelect: React.Dispatch<React.SetStateAction<string>>;
  onClose?: React.Dispatch<React.SetStateAction<boolean>>;
  excludedExtentions?: string[];
}) => {
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="group relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-200">
      {/* Media Preview */}
      <div className="aspect-square bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        {item.type === 'image' && (
          <img
            src={item.url}
            alt={item.originalName}
            className="max-w-full max-h-full object-contain rounded"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const parent = target.parentElement;
              if (parent) {
                const fallback = document.createElement('div');
                fallback.className = 'w-12 h-12 bg-gray-300 dark:bg-gray-600 rounded flex items-center justify-center';
                fallback.innerHTML = '📷';
                parent.appendChild(fallback);
              }
            }}
          />
        )}
        {item.type === 'video' && (
          <div className="w-12 h-12 bg-gray-300 dark:bg-gray-600 rounded flex items-center justify-center">
            🎥
          </div>
        )}
        {item.type === 'audio' && (
          <div className="w-12 h-12 bg-gray-300 dark:bg-gray-600 rounded flex items-center justify-center">
            🎵
          </div>
        )}
        {item.type === 'document' && (
          <div className="w-12 h-12 bg-gray-300 dark:bg-gray-600 rounded flex items-center justify-center">
            📄
          </div>
        )}
      </div>

      {/* File Info */}
      <div className="p-3">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={item.originalName}>
          {item.originalName}
        </p>
        <div className="flex justify-between items-center mt-2 text-xs text-gray-500 dark:text-gray-300">
          <span>{formatFileSize(item.size)}</span>
          <span>{formatDate(item.createdAt)}</span>
        </div>
      </div>

      {/* Actions */}
      {
        excludedExtentions && excludedExtentions.includes(item.mimeType) ?
          <div className="absolute bottom-0 left-0 right-0 bg-black/70 bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100 py-2">
            <p className="text-white text-xs">File type not allowed</p>
          </div>
          : (
            <div className="absolute bottom-0 left-0 right-0 bg-black/70 bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100 py-2">
              <div className="flex gap-2">
                <button
                  onClick={() => navigator.clipboard.writeText(item.url)}
                  className="p-2 rounded-lg transition-colors cursor-pointer"
                  title="Copy URL"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="9" height="13" x="9" y="9" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </button>
                <button
                  onClick={() => {
                    onSelect(item.url)
                    onClose?.(false)
                  }}
                  className="p-2 rounded-lg transition-colors cursor-pointer"
                  title="Select"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </button>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg transition-colors cursor-pointer"
                  title="View in new tab"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#06B6D4" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 3h6v6" />
                    <path d="M10 14 21 3" />
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  </svg>
                </a>
                {onDelete && (
                  <button
                    onClick={() => onDelete(item.id)}
                    className="p-2 rounded-lg transition-colors cursor-pointer"
                    title="Delete file"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 6h18" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                )}
                {/* {onClose && (
                  <button
                    onClick={() => onClose(false)}
                    className="p-2 rounded-lg transition-colors cursor-pointer"
                    title="Close"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6 6 18" />
                      <path d="m6 6 12 12" />
                    </svg>
                  </button>
                )} */}
              </div>
            </div>)}
    </div>
  );
};
